import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, X, Image as ImageIcon, Save } from "lucide-react";
import { ContentBlock } from "@/types/case-study";
import { useImageUpload } from "@/hooks/domains/storage/useImageUpload";
import { useAuth } from "@/hooks/domains/auth/useAuth";

interface ImageBlockProps {
  block: ContentBlock;
  onUpdate: (content: Record<string, unknown>) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
  preview?: boolean;
}

export const ImageBlock: React.FC<ImageBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
  preview = false,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Local state for caption editing
  const [localCaption, setLocalCaption] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Sync local caption with block content
  useEffect(() => {
    const blockCaption = (block.content?.caption as string) || "";
    setLocalCaption(blockCaption);
    setHasUnsavedChanges(false);
  }, [block.content?.caption]);
  
  const {
    uploading,
    handleImageUpload,
  } = useImageUpload({
    user,
    onImageUrlChange: (urls) => {
      if (urls.length > 0) {
        onUpdate({
          url: urls[0],
          caption: block.content?.caption || "",
        });
      }
    },
    bucketName: "case-study-images",
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleImageUpload(e);
  };

  const handleCaptionChange = (caption: string) => {
    setLocalCaption(caption);
    const hasChanges = caption !== ((block.content?.caption as string) || "");
    setHasUnsavedChanges(hasChanges);
  };

  const handleSaveCaption = () => {
    onUpdate({
      ...block.content,
      caption: localCaption,
    });
    setHasUnsavedChanges(false);
  };

  const imageUrl = block.content?.url as string;
  const caption = (block.content?.caption as string) || "";

  // Preview mode - only show image and caption
  if (preview) {
    if (!imageUrl) {
      return null; // Don't render anything if no image
    }

    return (
      <div className="space-y-3">
        <div className="relative">
          <img
            src={imageUrl}
            alt={caption || "Case study image"}
            className="w-full h-auto object-cover rounded-sm"
          />
        </div>
        {caption && (
          <div className="text-sm text-gray-600 italic text-center">
            {caption}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`group relative ${isActive ? '' : ''}`}>
      {isRemovable && onRemove && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-70 hover:opacity-100 transition-opacity hover:bg-red-600"
          title="Delete this content block"
        >
          ×
        </button>
      )}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        {imageUrl ? (
          <div className="space-y-3">
            <div className="relative">
              <img
                src={imageUrl}
                alt={caption || "Case study image"}
                className="w-full h-auto object-cover rounded-sm"
              />
            </div>
            <div className="flex gap-2">
              <Input
                value={localCaption}
                onChange={(e) => handleCaptionChange(e.target.value)}
                placeholder="Add a caption..."
                className="text-sm flex-1"
              />
              {hasUnsavedChanges && (
                <Button
                  onClick={handleSaveCaption}
                  size="sm"
                  className="px-3"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Upload an image, video, or GIF</p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              size="sm"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? "Uploading..." : "Choose File"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*,.gif"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}
      </div>
    </div>
  );
};
